import clsx from 'clsx';
import dot from 'dot-object';
import { debounce } from 'lodash';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useOdinMemo from '../../../common/hooks/use-odin-memo';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { TypeOrArray } from '../../common-interfaces';
import type { JsonDataItem } from '../../generic-types';
import TemplateOutlet from '../../template-outlet/template-outlet.component';
import Checkbox from '../checkbox/checkbox.component';
import type { DropDownOptionTemplateProps, DropDownOptionTemplateType, DropDownResult } from './drop-down-types';
import { DropDownSelectionType } from './drop-down-types';

export interface DropDownOptionProps {
  valueField: string;
  textField: string;
  archivedField?: string;
  data: JsonDataItem;
  className?: string;
  hoverStyle?: string;
  selectedStyle?: string;
  selectionType?: DropDownSelectionType;
  useCheckboxForMultiSelect?: boolean;
  selectedValue?: Array<DropDownResult>;
  highlightedValue?: Array<DropDownResult>;
  onChange?: (value: DropDownResult) => void;
  onItemDoubleClicked?: (value: DropDownResult) => void;
  optionTemplate?: DropDownOptionTemplateType;
}

const DropDownOption = ({
  valueField,
  textField,
  archivedField,
  data,
  className,
  hoverStyle = 'hover:odin-bg-gray-4 hover:odin-text-mono-0',
  selectedStyle = 'odin-bg-gray-2 odin-text-mono-1',
  selectionType = DropDownSelectionType.Single,
  useCheckboxForMultiSelect = true,
  selectedValue,
  highlightedValue,
  onChange,
  onItemDoubleClicked,
  optionTemplate,
}: DropDownOptionProps) => {
  const optionItemRef = useRef<HTMLDivElement>(null);
  const isCurrentSelection = useRef(false);
  const tagCreator = useTestTag(dot.pick(textField, data) || '');

  const text = useOdinMemo<string>(() => dot.pick(textField, data), [textField, data]);
  const value = useOdinMemo<unknown>(() => dot.pick(valueField, data), [valueField, data]);
  const archived = useOdinMemo(() => (archivedField ? !!dot.pick(archivedField, data) : false), [archivedField, data]);
  const isMultiSelect = selectionType === DropDownSelectionType.Multiple;

  const combinedRef = combineRefs(optionItemRef, tagCreator('drop-down-option'));

  const debouncedOnChange = debounce(() => {
    onChange?.({ value, text, archived, originalData: data });
  }, 300);

  const onItemSelected = () => {
    if (onItemDoubleClicked) {
      debouncedOnChange();
    } else {
      onChange?.({ value, text, archived, originalData: data });
    }
  };

  const onChangeMultiSelect = useCallback(() => {
    onChange?.({ value, text, archived, originalData: data });
  }, [value, text, archived, onChange, data]);

  const onDoubleClick = () => {
    if (onItemDoubleClicked) {
      debouncedOnChange.cancel();
      onItemDoubleClicked({ value, text, archived, originalData: data });
    }
  };

  const isSelected = () => {
    if (Array.isArray(selectedValue) && selectedValue.length > 0) {
      return !isMultiSelect
        ? selectedValue[0].value === value
        : selectedValue.find((item) => item.value === value) !== undefined;
    }

    return false;
  };

  // this will be used to determine if the current item should be highlighted but not selected
  const isHighlighted = () => {
    if (Array.isArray(highlightedValue) && highlightedValue.length > 0) {
      const itemHighlighted = !isMultiSelect
        ? highlightedValue[0].value === value
        : highlightedValue.find((item) => item.value === value) !== undefined;

      // I really don't like doing this because it feels like a hack, but I didn't like the alternative which was to
      // have a useEffect that ran for each option and called this function multiple times. We need to think of a better
      // way to accomplish this.
      if (itemHighlighted) {
        if (!isCurrentSelection.current) {
          isCurrentSelection.current = true;
          scrollHighlightedItemIntoView();
        }
      } else {
        isCurrentSelection.current = false;
      }

      return itemHighlighted;
    }

    return false;
  };

  const scrollHighlightedItemIntoView = () => {
    optionItemRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  };

  const optionStyle = clsx(
    'odin-py-1 odin-px-2',
    className,
    {
      [`odin-cursor-pointer ${hoverStyle}`]: !isMultiSelect || !useCheckboxForMultiSelect,
    },
    {
      [selectedStyle]: (isSelected() || isHighlighted()) && (!isMultiSelect || !useCheckboxForMultiSelect),
    },
    {
      'odin-pointer-events-none': (isSelected() || isHighlighted()) && !isMultiSelect,
      'odin-bg-primary-5': isHighlighted() && isMultiSelect && useCheckboxForMultiSelect,
      'hover:odin-bg-primary-5 odin-dropdown-checkbox-option': isMultiSelect && useCheckboxForMultiSelect,
    }
  );

  const dynamicProperties = useMemo<Record<string, unknown>>(() => {
    const properties: Record<string, unknown> = {};

    if (!isMultiSelect || useCheckboxForMultiSelect === false) {
      properties['onClick'] = onItemSelected;
      properties['onDoubleClick'] = onDoubleClick;
    }

    return properties;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onItemSelected and onDoubleClick are stable functions
  }, [isMultiSelect, useCheckboxForMultiSelect]);

  const optionTemplateProps: DropDownOptionTemplateProps = {
    value,
    text,
    archived,
    isMultiSelect,
    selectedValue,
    highlightedValue,
    onItemSelected,
    onDoubleClick,
    isSelected: isSelected(),
    isHighlighted: isHighlighted(),
  };

  const optionDefaultTemplate = useCallback(
    (_props: DropDownOptionTemplateProps, children?: TypeOrArray<ReactNode>) => <>{children}</>,
    []
  );

  return (
    <TemplateOutlet props={optionTemplateProps} template={optionTemplate || optionDefaultTemplate}>
      <div ref={combinedRef} className={optionStyle} {...dynamicProperties}>
        {!isMultiSelect || useCheckboxForMultiSelect === false ? (
          text
        ) : (
          <Checkbox
            name={`${text}-${value}`}
            label={text}
            initialChecked={isSelected()}
            useAnimation={false}
            onChange={!isMultiSelect ? onItemSelected : onChangeMultiSelect}
          />
        )}
      </div>
    </TemplateOutlet>
  );
};

export default DropDownOption;
